import { createContext, useContext, useLayoutEffect, useState, type ReactNode } from 'react';

export const INTERFACE_TEXT_SIZE_KEY = 'tableread.interfaceTextSize';
export const INTERFACE_TEXT_SIZES = ['small', 'medium', 'large'] as const;
export type InterfaceTextSize = typeof INTERFACE_TEXT_SIZES[number];
export const parseInterfaceTextSize = (value: unknown): InterfaceTextSize =>
  INTERFACE_TEXT_SIZES.includes(value as InterfaceTextSize) ? value as InterfaceTextSize : 'medium';
const Context = createContext({size: 'medium' as InterfaceTextSize, setSize: (_size: InterfaceTextSize) => {}});

/** Browser-only appearance preference, independent of screenplay content/zoom. */
export function InterfaceTextSizeProvider({children}: {children: ReactNode}) {
  const [size, updateSize] = useState<InterfaceTextSize>(() => {
    try { return parseInterfaceTextSize(localStorage.getItem(INTERFACE_TEXT_SIZE_KEY)); }
    catch { return 'medium'; }
  });
  useLayoutEffect(() => {
    const root = document.documentElement;
    const previous = root.getAttribute('data-interface-text-size');
    root.setAttribute('data-interface-text-size', size);
    return () => { if (previous === null) root.removeAttribute('data-interface-text-size'); else root.setAttribute('data-interface-text-size', previous); };
  }, [size]);
  useLayoutEffect(() => {
    const sync = (event: StorageEvent) => {
      if (event.storageArea === localStorage && (event.key === INTERFACE_TEXT_SIZE_KEY || event.key === null)) updateSize(parseInterfaceTextSize(event.newValue));
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);
  const setSize = (next: InterfaceTextSize) => {
    const valid = parseInterfaceTextSize(next);
    updateSize(valid);
    try { localStorage.setItem(INTERFACE_TEXT_SIZE_KEY, valid); } catch { /* Still usable for this session. */ }
  };
  return <Context.Provider value={{size, setSize}}>{children}</Context.Provider>;
}
export const useInterfaceTextSize = () => useContext(Context);
